import { cn } from '@/lib/utils'

interface LogoProps {
  variant?: 'full' | 'icon' | 'horizontal'
  size?: 'sm' | 'md' | 'lg' | 'xl'
  theme?: 'primary' | 'monochrome' | 'inverted'
  className?: string
}

const sizeClasses = {
  sm: 'h-8 w-8',
  md: 'h-12 w-12', 
  lg: 'h-16 w-16',
  xl: 'h-20 w-20'
}

const fullSizeClasses = {
  sm: 'h-8 w-32',
  md: 'h-12 w-48',
  lg: 'h-16 w-64', 
  xl: 'h-20 w-80'
}

const horizontalSizeClasses = {
  sm: 'h-6 w-24',
  md: 'h-8 w-32',
  lg: 'h-10 w-40',
  xl: 'h-12 w-48'
}

export function Logo({ 
  variant = 'full', 
  size = 'md', 
  theme = 'primary', 
  className 
}: LogoProps) {
  const getSizeClass = () => {
    switch (variant) {
      case 'icon':
        return sizeClasses[size]
      case 'horizontal':
        return horizontalSizeClasses[size]
      default:
        return fullSizeClasses[size]
    }
  }

  const getColors = () => {
    switch (theme) {
      case 'monochrome':
        return {
          primary: 'currentColor',
          secondary: 'currentColor',
          accent: 'currentColor',
          text: 'currentColor',
          gold: 'currentColor'
        }
      case 'inverted':
        return {
          primary: '#FFFFFF',
          secondary: '#F3F4F6',
          accent: '#E5E7EB',
          text: '#FFFFFF',
          gold: '#F3F4F6'
        }
      default:
        return {
          primary: '#0D7C47',     // Islamic green
          secondary: '#006B3C',   // Darker green
          accent: '#D4AF37',      // Islamic gold
          text: '#1C2821',        // Dark green-gray
          gold: '#B8860B'         // Rich gold
        }
    }
  }

  const colors = getColors()

  // Icon-only variant (Islamic geometric design)
  if (variant === 'icon') {
    return (
      <svg
        viewBox="0 0 100 100"
        className={cn(getSizeClass(), className)}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Outer circle with Islamic green */}
        <circle
          cx="50"
          cy="50"
          r="48"
          fill={colors.primary}
          stroke={colors.gold}
          strokeWidth="1.5"
        />
        
        {/* 8-pointed Islamic star (Khatam) */}
        <g transform="translate(50,50)">
          {/* Star points */}
          {[0, 45, 90, 135, 180, 225, 270, 315].map((rotation, index) => (
            <g key={index} transform={`rotate(${rotation})`}>
              <path
                d="M 0,-30 L 4,-12 L 0,-8 L -4,-12 Z"
                fill={colors.accent}
                opacity="0.9"
              />
            </g>
          ))}
          
          {/* Inner octagon */}
          <polygon
            points="-12,-12 -12,12 12,12 12,-12"
            fill={colors.secondary}
            transform="rotate(22.5)"
          />
          
          {/* Central circle for "53" */}
          <circle
            cx="0"
            cy="0"
            r="14"
            fill={colors.primary}
            stroke={colors.gold}
            strokeWidth="1"
          />
        </g>
        
        {/* Central "53" - more prominent */}
        <text
          x="50"
          y="59"
          textAnchor="middle"
          className="font-bold"
          fill={colors.accent}
          fontSize="22"
          fontFamily="system-ui, sans-serif"
        >
          53
        </text>
      </svg>
    )
  }

  // Horizontal variant (Islamic geometric logo + "53")
  if (variant === 'horizontal') {
    return (
      <svg
        viewBox="0 0 180 50"
        className={cn(getSizeClass(), className)}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Circular logo part */}
        <circle
          cx="25"
          cy="25"
          r="23"
          fill={colors.primary}
          stroke={colors.gold}
          strokeWidth="1"
        />
        
        {/* Simplified Islamic star pattern */}
        <g transform="translate(25,25)">
          {/* 4 main star points for compact design */}
          {[0, 90, 180, 270].map((rotation, index) => (
            <g key={index} transform={`rotate(${rotation})`}>
              <path
                d="M 0,-18 L 2,-8 L 0,-6 L -2,-8 Z"
                fill={colors.accent}
                opacity="0.8"
              />
            </g>
          ))}
          
          {/* Central area for "S" */}
          <circle
            cx="0"
            cy="0"
            r="8"
            fill={colors.secondary}
            stroke={colors.gold}
            strokeWidth="0.5"
          />
        </g>
        
        {/* "S" in the circle */}
        <text
          x="25"
          y="30"
          textAnchor="middle"
          className="font-bold"
          fill={colors.accent}
          fontSize="14"
          fontFamily="system-ui, sans-serif"
        >
          S
        </text>
        
        {/* "53" text - larger and more prominent */}
        <text
          x="60"
          y="35"
          className="font-bold"
          fill={colors.primary}
          fontSize="28"
          fontFamily="system-ui, sans-serif"
        >
          53
        </text>
      </svg>
    )
  }

  // Full variant (complete Islamic cooperative logo)
  return (
    <svg
      viewBox="0 0 280 80"
      className={cn(getSizeClass(), className)}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Main circular logo with Islamic design */}
      <circle
        cx="40"
        cy="40"
        r="38"
        fill={colors.primary}
        stroke={colors.gold}
        strokeWidth="1.5"
      />
      
      {/* Islamic 8-pointed star */}
      <g transform="translate(40,40)">
        {/* Star points */}
        {[0, 45, 90, 135, 180, 225, 270, 315].map((rotation, index) => (
          <g key={index} transform={`rotate(${rotation})`}>
            <path
              d="M 0,-28 L 3,-12 L 0,-8 L -3,-12 Z"
              fill={colors.accent}
              opacity="0.85"
            />
          </g>
        ))}
        
        {/* Inner geometric pattern */}
        <polygon
          points="-10,-10 -10,10 10,10 10,-10"
          fill={colors.secondary}
          transform="rotate(22.5)"
        />
        
        {/* Central circle for "S" */}
        <circle
          cx="0"
          cy="0"
          r="12"
          fill={colors.primary}
          stroke={colors.gold}
          strokeWidth="1"
        />
      </g>
      
      {/* "S" in circle */}
      <text
        x="40"
        y="48"
        textAnchor="middle"
        className="font-bold"
        fill={colors.accent}
        fontSize="18"
        fontFamily="system-ui, sans-serif"
      >
        S
      </text>
      
      {/* "Sharikah" text - more elegant */}
      <text
        x="95"
        y="32"
        className="font-semibold"
        fill={colors.text}
        fontSize="20"
        fontFamily="serif"
        style={{ letterSpacing: '0.5px' }}
      >
        Sharikah
      </text>
      
      {/* "53" large and prominent */}
      <text
        x="95"
        y="58"
        className="font-bold"
        fill={colors.primary}
        fontSize="32"
        fontFamily="system-ui, sans-serif"
      >
        53
      </text>
      
      {/* Subtitle */}
      <text
        x="95"
        y="71"
        className="font-medium"
        fill={colors.text}
        fontSize="9"
        fontFamily="system-ui, sans-serif"
        opacity="0.75"
      >
        Islamic Cooperative
      </text>
    </svg>
  )
}